'use client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useFactorToAPR } from '@/lib/hooks/useFactortoApr';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  getCalculatorAccordionStyles,
  OutputField,
  OutputGraphic,
  Styled2ColGrid,
  StyledColumn,
  StyledRow,
  StyledTextField,
  StyledTextSlider,
  StyledToggleButtonField,
} from './components';

const LoanTermType = {
  Days: 'days',
  Months: 'months',
} as const;
type LoanTermType = (typeof LoanTermType)[keyof typeof LoanTermType];

const PaymentFrequency = {
  Daily: 'daily',
  Weekly: 'weekly',
  Fortnightly: 'fortnightly',
  Monthly: 'monthly',
} as const;
type PaymentFrequency = (typeof PaymentFrequency)[keyof typeof PaymentFrequency];

type FactorToAprFormValues = {
  loanAmount: number;
  loanTerm: string;
  factorRate: number;
  loanTermType: LoanTermType;
  paymentFrequency: PaymentFrequency;
  adminFee: string;
  riskAssessmentFee: string;
  dischargeFee: string;
  loanGuaranteeFee: string;
  otherFee: string;
  monthlyFee: string;
};

const formatCurrency = (value: number | string) =>
  Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const FactorToApr = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();

  const methods = useForm<FactorToAprFormValues>({
    defaultValues: {
      loanAmount: 30000,
      loanTerm: '180',
      factorRate: 1.3,
      loanTermType: LoanTermType.Days,
      paymentFrequency: PaymentFrequency.Monthly,
      adminFee: '0',
      riskAssessmentFee: '0',
      dischargeFee: '0',
      loanGuaranteeFee: '0',
      otherFee: '0',
      monthlyFee: '0',
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const factorResult = useFactorToAPR({
    loanAmount: values.loanAmount || 0,
    factorRate: values.factorRate || 0,
    loanTerm: parseInt(values.loanTerm, 10) || 0,
    loanTermType: values.loanTermType,
    paymentFrequency: values.paymentFrequency,
    adminFee: parseFloat(values.adminFee) || 0,
    riskAssessmentFee: parseFloat(values.riskAssessmentFee) || 0,
    dischargeFee: parseFloat(values.dischargeFee) || 0,
    loanGuaranteeFee: parseFloat(values.loanGuaranteeFee) || 0,
    otherFees: parseFloat(values.otherFee) || 0,
    monthlyFee: parseFloat(values.monthlyFee) || 0,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="FactorToApr"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Select loan terms'}>
          <Controller
            name="loanAmount"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Loan Amount"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
                inputProps={{ inputMode: 'numeric' }}
              />
            )}
          />

          <Controller
            name="factorRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Factor Rate"
                value={field.value}
                onChange={field.onChange}
                inputProps={{ placeholder: 'e.g. 1.2', step: 0.01, min: 1 }}
                step={0.01}
                minValue={1}
                defaultMax={2}
              />
            )}
          />
          <StyledRow
            sx={{
              justifyContent: 'flex-start',
            }}
          >
            <Controller
              name="loanTerm"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  label="Loan Term"
                  value={field.value}
                  onChange={(value) => {
                    if (/^\d*$/.test(value)) field.onChange(value);
                  }}
                  type="number"
                  stepper={1}
                  boxSx={{ maxWidth: '8.5rem' }}
                  inputProps={{ min: 1, step: 1, sx: {} }}
                />
              )}
            />

            <Controller
              name="loanTermType"
              control={control}
              render={({ field }) => (
                <StyledToggleButtonField
                  label="Term Unit"
                  value={field.value}
                  onChange={(value) => field.onChange(value as LoanTermType)}
                  options={[
                    { value: LoanTermType.Days, label: 'Days' },
                    { value: LoanTermType.Months, label: 'Months' },
                  ]}
                  sx={{ maxWidth: '12.5rem' }}
                />
              )}
            />
          </StyledRow>

          <Controller
            name="paymentFrequency"
            control={control}
            render={({ field }) => (
              <StyledToggleButtonField
                label="Payment Frequency"
                value={field.value}
                onChange={(value) => field.onChange(value as PaymentFrequency)}
                sx={{
                  mb: 3,
                  '& .MuiToggleButtonGroup-root': {
                    gridTemplateColumns: { xs: '1fr 1fr 1fr 1fr' },
                  },
                }}
                options={[
                  { value: PaymentFrequency.Daily, label: 'Daily' },
                  { value: PaymentFrequency.Weekly, label: 'Weekly' },
                  { value: PaymentFrequency.Fortnightly, label: 'Fortnightly' },
                  { value: PaymentFrequency.Monthly, label: 'Monthly' },
                ]}
              />
            )}
          />

          <Accordion sx={getCalculatorAccordionStyles(theme)}>
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ fontSize: 30, color: theme.palette.common.black }} />
              }
            >
              <Typography variant="small">Optional Fee(s)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Styled2ColGrid sx={{ color: theme.palette.common.black }}>
                <Controller
                  name="adminFee"
                  control={control}
                  render={({ field }) => (
                    <StyledTextField
                      label="Admin Fee"
                      prefix="$"
                      value={field.value}
                      onChange={field.onChange}
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  )}
                />
                <Controller
                  name="riskAssessmentFee"
                  control={control}
                  render={({ field }) => (
                    <StyledTextField
                      label="Risk Assessment Fee"
                      prefix="$"
                      value={field.value}
                      onChange={field.onChange}
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  )}
                />
                <Controller
                  name="dischargeFee"
                  control={control}
                  render={({ field }) => (
                    <StyledTextField
                      label="Discharge Fee"
                      prefix="$"
                      value={field.value}
                      onChange={field.onChange}
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  )}
                />
                <Controller
                  name="loanGuaranteeFee"
                  control={control}
                  render={({ field }) => (
                    <StyledTextField
                      label="Loan Guarantee Fee"
                      prefix="$"
                      value={field.value}
                      onChange={field.onChange}
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  )}
                />
                <Controller
                  name="otherFee"
                  control={control}
                  render={({ field }) => (
                    <StyledTextField
                      label="Other Fee"
                      prefix="$"
                      value={field.value}
                      onChange={field.onChange}
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  )}
                />
                <Controller
                  name="monthlyFee"
                  control={control}
                  render={({ field }) => (
                    <StyledTextField
                      label="Monthly Fee"
                      prefix="$"
                      value={field.value}
                      onChange={field.onChange}
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  )}
                />
              </Styled2ColGrid>
            </AccordionDetails>
          </Accordion>
        </CalculatorInputs>

        <CalculatorOutputs
          title={block?.CustomOutputsTitle ?? 'Factor to APR Summary'}
          sx={{ maxWidth: { xs: '100%', md: '100%' } }}
        >
          {factorResult && (
            <>
              <Styled2ColGrid>
                <OutputGraphic
                  data={[
                    {
                      label: 'Principal',
                      value: parseFloat(factorResult.principle),
                      color: theme.palette.brand.primary,
                    },
                    {
                      label: 'Interest & Fees',
                      value: parseFloat(factorResult.totalInterestFees),
                      color: theme.palette.brand.secondary,
                    },
                  ]}
                />
                <StyledColumn>
                  <OutputField
                    label="Interest & Fees"
                    value={formatCurrency(factorResult.totalInterestFees)}
                  />
                  <OutputField
                    label="Total Payback"
                    value={formatCurrency(factorResult.totalCost)}
                  />
                </StyledColumn>
                <OutputField label="Interest Rate" value={`${factorResult.interestRate}%`} />
                <OutputField label="APR" value={`${factorResult.APR}%`} highlight />
              </Styled2ColGrid>
              <OutputField
                label="Repayments"
                value={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      component="em"
                      sx={{
                        color: `${theme.palette.common.white} !important`,
                      }}
                    >
                      {factorResult.numPayments}
                    </Box>
                    <Box
                      component="em"
                      sx={{
                        fontWeight: 400,
                        color: `${theme.palette.common.white}CC !important`,
                      }}
                    >
                      {' '}
                      payments of{' '}
                    </Box>
                    <Box
                      component="em"
                      sx={{
                        color: `${theme.palette.common.white} !important`,
                      }}
                    >
                      {formatCurrency(factorResult.repaymentAmount)}
                    </Box>
                  </Box>
                }
              />
            </>
          )}
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default FactorToApr;
